/**
 * One check-in / check-out control, used in four places: the header (every hub
 * page), the main dashboard, the People dashboard and the Attendance page.
 * All of them read the same attendance store, so checking in anywhere updates
 * every copy at once.
 *
 * Speed: the button flips the moment it is clicked (optimistic update in the
 * store); the GPS reading is usually ready already (see lib/fastLocation) and
 * the server confirms in the background. If the server refuses, the button
 * flips back and the reason is shown.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Play, Square, Clock } from 'lucide-react';
import { Button } from '../Button';
import { Avatar, AvatarFallback, AvatarImage } from '../Avatar';
import { getUserAvatarUrl, getUserInitials } from '../../utils/avatar';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../Dialog';
import { getFastLocation, primeLocation, startLocationPriming } from '../../lib/fastLocation';
import { useAttendanceStore, type AttendanceError } from '../../store/attendanceStore';

const elapsed = (minutes: number) => `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;

export function useCheckInOut(onDone?: () => void) {
    const { today, todayLoading, acting, fetchToday, checkIn, checkOut } = useAttendanceStore();
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        startLocationPriming();
        const s = useAttendanceStore.getState();
        if (!s.today && !s.todayLoading) fetchToday().catch(() => undefined);
    }, [fetchToday]);

    const working = today?.state === 'CHECKED_IN';
    const available = !!today && today.rules[working ? 'checkOutEnabled' : 'checkInEnabled'];

    const toggle = async () => {
        if (!today || acting) return;
        const gps = getFastLocation().then((fix) => fix ?? {});
        try {
            const session = working ? await checkOut(gps) : await checkIn(gps);
            const place = working ? session.checkOutLocation : session.checkInLocation;
            toast.success(`${working ? 'Checked out' : 'Checked in'}${place ? ` · ${place}` : ''}`, { id: 'attendance' });
        } catch (e) {
            toast.error((e as AttendanceError).message, { id: 'attendance', duration: 7000 });
        }
    };

    // Ask first. Opening the dialog also warms up the GPS reading in the
    // background, so by the time the person confirms the location is usually
    // ready and the check-in/out goes through instantly.
    const requestToggle = () => {
        if (!today || acting) return;
        primeLocation().catch(() => undefined);
        setConfirmOpen(true);
    };
    const confirm = () => {
        setConfirmOpen(false);
        void toggle().then(() => onDone?.());
    };

    const confirmDialog = (
        <CheckInOutConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} working={!!working} onConfirm={confirm} />
    );

    return { today, loading: todayLoading && !today, acting, working, available, toggle, requestToggle, confirmDialog };
}

/** Confirmation asked before every check-in and check-out. */
function CheckInOutConfirmDialog({ open, onOpenChange, working, onConfirm }: {
    open: boolean; onOpenChange: (v: boolean) => void; working: boolean; onConfirm: () => void;
}) {
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[420px]">
                <DialogHeader>
                    <DialogTitle>{working ? 'Check out now?' : 'Check in now?'}</DialogTitle>
                    <DialogDescription>
                        {working
                            ? `This will close your current session and record your check-out at ${now}.`
                            : `This will start a new session and record your check-in at ${now}.`}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="secondary" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button size="sm" onClick={onConfirm} aria-label={working ? 'Confirm check out' : 'Confirm check in'}>
                        {working ? <Square /> : <Play />}
                        {working ? 'Check out' : 'Check in'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Total seconds worked today across sessions, ticking live while checked in. */
function useLiveWorkedSeconds(today: ReturnType<typeof useAttendanceStore.getState>['today']) {
    const [, force] = useState(0);
    const working = today?.state === 'CHECKED_IN';
    useEffect(() => {
        if (!working) return undefined;
        const t = setInterval(() => force((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, [working]);
    if (!today) return 0;
    let ms = 0;
    for (const s of today.sessions) {
        if (s.status === 'SUPERSEDED') continue;
        const start = s.checkIn ? new Date(s.checkIn).getTime() : 0;
        if (!start) continue;
        const end = s.checkOut ? new Date(s.checkOut).getTime() : (s.status === 'OPEN' ? Date.now() : start);
        ms += Math.max(0, end - start);
    }
    return Math.floor(ms / 1000);
}

const hms = (total: number) => {
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
};

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The current Sun–Sat week, with today and weekends flagged. */
function useWeek() {
    const now = new Date();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - now.getDay());
    sunday.setHours(0, 0, 0, 0);
    const todayKey = now.toDateString();
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(sunday);
        d.setDate(sunday.getDate() + i);
        return { label: WD[i], date: d.getDate(), isToday: d.toDateString() === todayKey, isWeekend: i === 0 || i === 6 };
    });
}

/**
 * Attendance hero for the main dashboard: who you are, a live running clock for
 * time worked today, the one check-in / check-out action, and the work week with
 * today marked. Stays visible for any employee (clear message when attendance
 * isn't set up) — the control is never hidden the way the top-bar button can be.
 */
export function CheckInOutCard({ className }: { className?: string }) {
    const { today, loading, working, available, requestToggle, confirmDialog } = useCheckInOut();
    const user = useAuthStore((s) => s.user);
    const seconds = useLiveWorkedSeconds(today);
    const week = useWeek();

    const state = today?.state;
    const status = working
        ? { label: 'Checked in', cls: 'bg-success/10 text-success-strong', dot: 'bg-success' }
        : state === 'CHECKED_OUT'
            ? { label: 'Checked out', cls: 'bg-secondary text-muted-foreground', dot: 'bg-muted-foreground' }
            : { label: 'Yet to check in', cls: 'bg-warning/10 text-warning-strong', dot: 'bg-warning' };

    const checkedInAt = today?.activeSession?.checkIn
        ? new Date(today.activeSession.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;

    return (
        <div className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
            <div className="flex flex-col gap-5 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                {/* Identity + status */}
                <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="size-12 shrink-0">
                        <AvatarImage src={user?.avatar || getUserAvatarUrl(user?.name, user?.email)} alt={user?.name || 'You'} />
                        <AvatarFallback className="bg-primary text-primary-foreground font-medium">
                            {getUserInitials(user?.name, user?.email)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                        <p className="truncate font-display text-card-title text-foreground">{user?.name || 'Your attendance'}</p>
                        {loading ? (
                            <p className="mt-1 text-xs text-muted-foreground">Loading today’s status…</p>
                        ) : !today ? (
                            <p className="mt-1 text-xs text-muted-foreground">Attendance isn’t set up for your account.</p>
                        ) : (
                            <span className={cn('mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium', status.cls)}>
                                <span className={cn('size-1.5 rounded-full', status.dot)} aria-hidden />
                                {status.label}
                            </span>
                        )}
                    </div>
                </div>

                {/* Live timer + action */}
                {today && (
                    <div className="flex items-center gap-4 sm:gap-5">
                        <div className="text-right">
                            <p className="font-display text-metric leading-none tabular-nums text-foreground">{hms(seconds)}</p>
                            <p className="mt-1 flex items-center justify-end gap-1 text-xs text-muted-foreground">
                                <Clock className="size-3" />
                                {working && checkedInAt ? `Since ${checkedInAt}` : seconds > 0 ? 'Worked today' : 'Not started yet'}
                            </p>
                        </div>
                        <Button
                            variant={working ? 'secondary' : 'primary'}
                            onClick={requestToggle}
                            disabled={!available}
                            title={!available ? 'Check-in/out isn’t available right now' : undefined}
                            className="shrink-0"
                            aria-label={working ? 'Check out' : 'Check in'}
                        >
                            {working ? <Square /> : <Play />}
                            {working ? 'Check out' : 'Check in'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Work week */}
            <div className="grid grid-cols-7 border-t border-border bg-background/40">
                {week.map((d, i) => (
                    <div key={i} className={cn('flex flex-col items-center gap-1 py-2.5', i < 6 && 'border-r border-border')}>
                        <span className="text-xs font-medium text-muted-foreground">{d.label}</span>
                        <span
                            className={cn(
                                'flex size-7 items-center justify-center rounded-full text-sm tabular-nums',
                                d.isToday ? 'bg-primary font-semibold text-primary-foreground'
                                    : d.isWeekend ? 'text-muted-foreground/60' : 'text-foreground',
                            )}
                        >
                            {d.date}
                        </span>
                    </div>
                ))}
            </div>
            {confirmDialog}
        </div>
    );
}

/** Minutes since `since`, updating every 30 s. */
export function useOpenMinutes(since: string | null | undefined) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!since) return undefined;
        setNow(Date.now());
        const t = setInterval(() => setNow(Date.now()), 30_000);
        return () => clearInterval(t);
    }, [since]);
    return since ? Math.max(0, Math.round((now - new Date(since).getTime()) / 60000)) : 0;
}

/**
 * Compact control for top bars: status dot + time worked in this session +
 * button. Renders nothing for people without an employee profile.
 */
export function CheckInOutButton({ className }: { className?: string }) {
    const { today, working, available, requestToggle, confirmDialog } = useCheckInOut();
    const minutes = useOpenMinutes(today?.activeSession?.checkIn);
    // Only hide for accounts with no attendance profile at all (today never
    // loads). When attendance IS loaded but check-in/out isn't enabled right
    // now, keep the control visible but disabled so it never silently vanishes.
    if (!today) return null;

    return (
        <div className={cn('flex items-center gap-2', className)}>
            {working && (
                <span className="hidden items-center gap-1.5 text-xs text-muted-foreground md:inline-flex" title="Checked in — time in this session">
                    <span className="size-2 rounded-full bg-success" aria-hidden />
                    <span className="tabular-nums text-foreground">{elapsed(minutes)}</span>
                </span>
            )}
            <Button variant={working ? 'secondary' : 'primary'} size="sm" onClick={requestToggle} disabled={!available}
                className="px-3 shrink-0"
                title={!available ? 'Check-in/out isn’t available right now' : undefined}
                aria-label={working ? 'Check out' : 'Check in'}>
                {working ? <Square /> : <Play />}
                <span>{working ? 'Check out' : 'Check in'}</span>
            </Button>
            {confirmDialog}
        </div>
    );
}
